import type { APIDesignerStore } from '.';
import type { MoveAction } from './BodySchemaEditor/utils';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { OpenAPIV3 } from 'openapi-types';

export const SCHEMA_TYPES = [
  'array',
  'boolean',
  'integer',
  'number',
  'object',
  'string',
] as const satisfies OpenAPIV3.SchemaObject['type'][];

export type SchemaType = (typeof SCHEMA_TYPES)[number];

export type SchemaFormat =
  | 'binary'
  | 'byte'
  | 'date-time'
  | 'date'
  | 'double'
  | 'float'
  | 'int32'
  | 'int64'
  | 'json'
  | 'password';

interface BasicType {
  icon: string;
  type: SchemaType;
}

interface PrimitiveType extends BasicType {
  format: SchemaFormat;
}

interface NumberType extends PrimitiveType {
  type: 'integer' | 'number';
}

interface StringType extends PrimitiveType {
  type: 'string';
}

export const basicTypes = [
  {
    icon: 'string',
    type: 'string',
  },
  {
    icon: 'integer',
    type: 'number',
  },
  {
    icon: 'integer',
    type: 'integer',
  },
  {
    icon: 'binary',
    type: 'boolean',
  },
] as const satisfies BasicType[];

export const numberTypes = [
  {
    icon: 'life-buoy',
    type: 'number',
    format: 'float',
  },
  {
    icon: 'double',
    type: 'number',
    format: 'double',
  },
  {
    icon: 'integer',
    type: 'integer',
    format: 'int32',
  },
  {
    icon: 'integer',
    type: 'integer',
    format: 'int64',
  },
] as const satisfies NumberType[];

export const stringTypes = [
  {
    icon: 'calendar',
    type: 'string',
    format: 'date',
  },
  {
    icon: 'clock',
    type: 'string',
    format: 'date-time',
  },
  {
    icon: 'lock',
    type: 'string',
    format: 'password',
  },
  {
    icon: 'string',
    type: 'string',
    format: 'byte',
  },
  {
    icon: 'binary',
    type: 'string',
    format: 'binary',
  },
  {
    icon: 'json',
    type: 'string',
    format: 'json',
  },
] as const satisfies StringType[];

export type EnumValue = NonNullable<OpenAPIV3.SchemaObject['enum']>[number];

export function isEnumTypeSupported(type: OpenAPIV3.SchemaObject['type']): boolean {
  return basicTypes.some(basicType => basicType.type === type);
}

export type SupportedTypes = boolean | number | string;
// Type conversions
function convertBooleanToType(value: boolean, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'string':
      return value.toString();
    case 'integer':
    case 'number':
    case 'boolean':
    default:
      return value;
  }
}

function convertNumberToType(value: number, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'string':
      return value.toString();
    case 'boolean':
      if (value === 1) return true;
      else if (value === 0) return false;
      return value;
    case 'integer':
    case 'number':
    default:
      return value;
  }
}

function convertStringToType(value: string, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'boolean':
      if (value.toLowerCase() === 'true') return true;
      else if (value.toLowerCase() === 'false') return false;
      return value;
    case 'integer': {
      const intNum = Number(value);
      return Number.isNaN(intNum) || !Number.isInteger(intNum) ? value : intNum;
    }
    case 'number': {
      const num = Number(value);
      return Number.isNaN(num) ? value : num;
    }
    case 'string':
    default:
      return value;
  }
}

// Converts a type to a different type if possible
// If not possible, returns the original value with the original type
export function convertToType(value: EnumValue, type: SchemaType | undefined): SupportedTypes {
  // If the value is already the correct type or the type is not supported, return the original value
  const valueType = typeof value;
  if (valueType === type || !type || !isEnumTypeSupported(type)) {
    return value;
  }

  switch (typeof value) {
    case 'boolean':
      return convertBooleanToType(value, type);
    case 'number':
      return convertNumberToType(value, type);
    case 'string':
      return convertStringToType(value, type);
    default:
      return value;
  }
}

/**
 * Moves a parameter from one position to another in the parameters array
 * @param parameters - The filtered parameters array
 * @param allParams - The full parameters array to modify (can be Immer WritableDraft types)
 * @param moveAction - The move action containing source and target positions
 */
export function moveParameter(
  inParameters: (WritableDraft<OpenAPIV3.ParameterObject> | WritableDraft<OpenAPIV3.ReferenceObject>)[],
  allParams: (WritableDraft<OpenAPIV3.ParameterObject> | WritableDraft<OpenAPIV3.ReferenceObject>)[],
  moveAction: MoveAction,
): void {
  const { source, target } = moveAction;

  if (source.parentId !== target.parentId) {
    throw new Error('Cannot move parameter between different parents');
  }

  // Get the actual parameter objects from the filtered array
  const sourceParam = inParameters[source.position];
  const targetParam = inParameters[target.position];

  if (!sourceParam || !targetParam) {
    throw new Error('Invalid position for move operation');
  }

  // Find the indices of these parameters in the full parameters array
  const sourceIndex = allParams.findIndex(p => p === sourceParam);
  const targetIndex = allParams.findIndex(p => p === targetParam);

  if (sourceIndex === -1 || targetIndex === -1) {
    throw new Error('Invalid position for move operation');
  }

  // Update position in the full parameters array
  allParams.splice(sourceIndex, 1);
  allParams.splice(targetIndex, 0, sourceParam);
}

/**
 * Generates a unique name with a given base name that doesn't conflict with existing names.
 * Uses the pattern 'baseName', 'baseName1', 'baseName2', etc.
 * @param baseName - The base name to use (e.g., 'name', 'param')
 * @param existingNames - Either an array of existing names or an object with existing property names
 * @returns A unique name
 */
export function generateUniqueName(baseName: string, existingNames: Record<string, unknown> | string[]): string {
  const nameExists = (testName: string): boolean => {
    if (Array.isArray(existingNames)) {
      return existingNames.includes(testName);
    }
    return testName in existingNames;
  };

  // First try the base name without any number
  if (!nameExists(baseName)) {
    return baseName;
  }

  // If base name exists, start from 1
  let counter = 1;
  let name = `${baseName}${counter}`;

  while (nameExists(name)) {
    counter += 1;
    name = `${baseName}${counter}`;
  }

  return name;
}

export function generateUniqueParameterName(existingParameterNames: string[]): string {
  return generateUniqueName('param', existingParameterNames);
}

/**
 * Moves a property from one position to another in an object's properties
 * @param properties - The object properties to modify
 * @param moveAction - The move action containing source and target positions
 */
export function moveParameterInObject(
  properties: WritableDraft<Record<string, OpenAPIV3.ReferenceObject | OpenAPIV3.SchemaObject>>,
  moveAction: MoveAction,
): void {
  const { source, target } = moveAction;

  // Get the property names as an array to maintain order
  const propertyNames = [...Object.keys(properties)];

  if (source.parentId !== target.parentId) {
    throw new Error('Cannot move property between different parents');
  }

  // Validate positions
  if (
    source.position < 0 ||
    source.position >= propertyNames.length ||
    target.position < 0 ||
    target.position >= propertyNames.length
  ) {
    throw new Error('Invalid position for move operation');
  }

  // Get the property names at source and target positions
  const sourcePropertyName = propertyNames[source.position];

  // Since JavaScript objects maintain insertion order, we need to rebuild the object
  // We can do this by creating a new object with the correct order and then
  // replacing the content of the existing object

  // Remove the source property from its current position
  propertyNames.splice(source.position, 1);

  // Insert the source property at the target position
  propertyNames.splice(target.position, 0, sourcePropertyName);

  // Now rebuild the properties object using the reordered property names
  const reorderedProperties: Record<string, OpenAPIV3.ReferenceObject | OpenAPIV3.SchemaObject> = {};

  // Iterate through the reordered property names to build the new properties object
  propertyNames.forEach(propertyName => {
    reorderedProperties[propertyName] = properties[propertyName];
  });

  // Since we're working with Immer WritableDraft, we need to modify the original object in-place
  // Clear all existing properties
  Object.keys(properties).forEach(key => delete properties[key]);

  // Assign the reordered properties to the original object
  Object.assign(properties, reorderedProperties);
}

/**
 * Helper function to get the schema property mapping of the parent of the given schema property mapping.
 *
 * For example, if the schemaPropertyMapping is `0`, then this is the top level schema and has no parent.
 * We can ignore this case and just return `-1` because the top level object should never be dragged.
 * If the schemaPropertyMapping is `0-1`, then its parent is `0` and local position is `1`.
 * If the schemaPropertyMapping is `0-1-5`, then its parent is `0-1` and local position is `5`.
 */
export function getSchemaPositionInfo(schemaPropertyMapping: string): {
  parentMapping: string;
  localPosition: number;
} {
  // if top level schema, return -1 to indicate no parent
  if (schemaPropertyMapping === '0') {
    return { parentMapping: '-1', localPosition: 0 };
  }

  const segments = schemaPropertyMapping.split('-');

  if (segments.length < 1) {
    throw new Error('Invalid schema property mapping');
  }

  const lastSegment = segments.pop();

  if (!lastSegment) {
    throw new Error('Invalid schema property mapping');
  }

  const localPosition = parseInt(lastSegment, 10);

  return {
    parentMapping: segments.length > 0 ? segments.join('-') : '0',
    localPosition,
  };
}

/**
 * Validates the API Designer state and returns the validated values.
 * This function can be used to just validate the state or to validate the state and return the validated values.
 * Throws an error if any required properties are missing.
 */
export const getValidatedAPIDesignerState = (state: APIDesignerStore) => {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  return {
    apiObject: state.apiObject,
    schema: state.apiObject.schema,
    paths: state.apiObject.schema.paths,
    path: currentPath,
    method: currentMethod,
    operation: state.apiObject.schema.paths[currentPath][currentMethod] as OpenAPIV3.OperationObject,
  };
};
